// frontend/src/Dashboard/InstructorDashboard.jsx
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  BarChart3,
  Users,
  Send,
  Megaphone,
  Mail,
  MessageSquare,
  Bell,
  FileText,
  Search,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Clock,
  Award,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Building2,
  User,
  Download,
} from "lucide-react";

import Footer from "../components/Footer";
import { getInstructorLearners, sendReminder } from "../services/instructorApi";
import {
  instructorListAnnouncements,
  instructorCreateAnnouncement,
} from "../services/announcementsApi";

/* ----------------------------- small helpers ----------------------------- */
function safeStr(v) {
  return v == null ? "" : String(v);
}
function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}
function pct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}
function nameOf(l) {
  return (
    l?.fullName ||
    l?.full_name ||
    l?.name ||
    [l?.first_name, l?.last_name].filter(Boolean).join(" ") ||
    "—"
  );
}
function emailOf(l) {
  return l?.email || l?.email_address || "—";
}
function keyOf(l, i) {
  return l?.id || l?.user_id || l?.clerk_id || `${emailOf(l)}-${i}`;
}

/**
 * Try to extract course enrollment/progress info from a learner row if available.
 * Supports a few shapes without breaking if fields don't exist.
 */
function extractCourseBuckets(learners) {
  const buckets = new Map(); // courseSlug -> { title, enrolled, active, graduated }
  for (const l of learners || []) {
    const arr = Array.isArray(l?.courses)
      ? l.courses
      : Array.isArray(l?.course_progress)
      ? l.course_progress
      : null;

    if (arr && arr.length) {
      for (const c of arr) {
        const slug = safeStr(c?.slug || c?.course_slug || c?.id).toLowerCase();
        if (!slug) continue;

        const title = c?.title || slug.replace(/-/g, " ");
        const prog = pct(c?.progress ?? c?.completion ?? l?.progress ?? 0);
        const grad = !!(c?.graduated ?? c?.is_graduated);
        const active = !grad && prog > 0;

        if (!buckets.has(slug)) {
          buckets.set(slug, { slug, title, enrolled: 0, active: 0, graduated: 0 });
        }
        const b = buckets.get(slug);
        b.enrolled += 1;
        if (active) b.active += 1;
        if (grad) b.graduated += 1;
      }
      continue;
    }

    const singleSlug = safeStr(
      l?.course_slug || l?.current_course || l?.courseId || l?.course_id
    ).toLowerCase();

    if (singleSlug) {
      const title = singleSlug.replace(/-/g, " ");
      const prog = pct(l?.progress ?? l?.completion ?? 0);
      const grad = !!l?.graduated;
      const active = !grad && prog > 0;

      if (!buckets.has(singleSlug)) {
        buckets.set(singleSlug, { slug: singleSlug, title, enrolled: 0, active: 0, graduated: 0 });
      }

      const b = buckets.get(singleSlug);
      b.enrolled += 1;
      if (active) b.active += 1;
      if (grad) b.graduated += 1;
    }
  }
  return [...buckets.values()].sort((a, b) => (b.enrolled || 0) - (a.enrolled || 0));
}

/* --------------------------------- UI ---------------------------------- */
const SECTIONS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "learners", label: "Learners", icon: Users },
  { id: "comms", label: "Communication", icon: Send },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "reports", label: "Reports", icon: FileText },
  { id: "courses", label: "Course Analytics", icon: TrendingUp },
];

export default function InstructorDashboard() {
  const { getToken } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [active, setActive] = useState("overview");

  // Global search (top bar)
  const [globalQ, setGlobalQ] = useState("");
  const globalQRef = useRef("");
  useEffect(() => {
    globalQRef.current = globalQ;
  }, [globalQ]);

  // Learners data
  const [learners, setLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  // Learners filters
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | dormant | graduated
  const [expanded, setExpanded] = useState(null);

  // Communication UI state
  const [commsTab, setCommsTab] = useState("notifications"); // notifications | sms | email
  const [targetMode, setTargetMode] = useState("individual"); // individual | church
  const [targetLearnerId, setTargetLearnerId] = useState("");
  const [commsSubject, setCommsSubject] = useState("");
  const [commsMessage, setCommsMessage] = useState("");
  const [commsSending, setCommsSending] = useState(false);

  // Announcements (church scope)
  const [annList, setAnnList] = useState([]);
  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");
  const [annLoading, setAnnLoading] = useState(false);

  // ✅ safer toast timer
  const toastTimerRef = useRef(null);
  function flash(msg) {
    setToast(msg);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(""), 2800);
  }
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  /* ------------------------------ load learners ------------------------------ */
  const loadLearners = useCallback(async () => {
    try {
      setLoadingLearners(true);
      setErr("");
      const token = await getToken();
      const res = await getInstructorLearners(token);
      const arr = Array.isArray(res?.data ?? res) ? (res.data ?? res) : [];
      setLearners(arr);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load learners");
      setLearners([]);
    } finally {
      setLoadingLearners(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadLearners();
  }, [loadLearners]);

  /* ---------------------------- load announcements --------------------------- */
  const loadAnnouncements = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await instructorListAnnouncements(token);
      const arr = Array.isArray(res?.data ?? res) ? (res.data ?? res) : [];
      setAnnList(arr);
    } catch (e) {
      console.error(e);
      setAnnList([]);
    }
  }, [getToken]);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  /* ---------------------------------- stats --------------------------------- */
  const stats = useMemo(() => {
    const total = learners.length;
    const completed = learners.filter((l) => !!l.graduated).length;
    const activeCount = learners.filter((l) => !l.graduated && pct(l.progress) > 0).length;
    const dormant = learners.filter((l) => !l.graduated && pct(l.progress) === 0).length;

    const avgProgress = total
      ? Math.round(
          learners.reduce(
            (sum, l) => sum + (Number.isFinite(Number(l.progress)) ? Number(l.progress) : 0),
            0
          ) / total
        )
      : 0;

    const lastActiveSorted = [...learners]
      .map((l) => new Date(l.lastActive || l.last_active || l.updated_at || l.created_at || 0).getTime())
      .filter((t) => Number.isFinite(t) && t > 0)
      .sort((a, b) => b - a);

    const lastActive = lastActiveSorted[0] ? new Date(lastActiveSorted[0]).toLocaleString() : "—";

    return {
      total,
      completed,
      activeCount,
      dormant,
      avgProgress: Math.min(100, Math.max(0, avgProgress)),
      lastActive,
    };
  }, [learners]);

  const courseBuckets = useMemo(() => extractCourseBuckets(learners), [learners]);

  // Best-effort church info
  const churchInfo = useMemo(() => {
    const sample = learners.find(Boolean) || {};
    const churchName =
      sample?.church_name ||
      sample?.church ||
      sample?.churchName ||
      sample?.church_title ||
      "Your Church";

    const districtName = sample?.district_name || sample?.district || sample?.districtName || null;
    const conferenceName = sample?.conference_name || sample?.conference || sample?.conferenceName || null;

    return { churchName, districtName, conferenceName };
  }, [learners]);

  /* -------------------------- learners search/filter ------------------------- */
  const filteredLearners = useMemo(() => {
    const q = safeStr(globalQ).trim().toLowerCase();

    const base = (learners || []).filter((l) => {
      const prog = pct(l.progress);
      const grad = !!l.graduated;

      if (statusFilter === "graduated") return grad;
      if (statusFilter === "active") return !grad && prog > 0;
      if (statusFilter === "dormant") return !grad && prog === 0;

      return true;
    });

    if (!q) return base;

    return base.filter((l) => {
      const hay = [
        nameOf(l),
        emailOf(l),
        l?.phone,
        l?.id,
        l?.clerk_id,
        l?.user_id,
        l?.church_name,
        l?.district_name,
      ]
        .map((x) => safeStr(x).toLowerCase())
        .join(" | ");
      return hay.includes(q);
    });
  }, [learners, globalQ, statusFilter]);

  /* --------------------------------- actions -------------------------------- */
  async function onRemind(learnerId, message) {
    try {
      const token = await getToken();
      await sendReminder(token, { learnerId, ...(message ? { message } : {}) });
      flash("Reminder queued!");
    } catch (e) {
      console.error(e);
      flash(e?.message || "Failed to send reminder");
    }
  }

  async function onCreateAnnouncement(e) {
    e.preventDefault();
    const title = annTitle.trim();
    const body = annBody.trim();
    if (!title || !body) return;

    try {
      setAnnLoading(true);
      const token = await getToken();
      await instructorCreateAnnouncement(token, { title, body });
      setAnnTitle("");
      setAnnBody("");
      await loadAnnouncements();
      flash("Announcement published!");
      setActive("announcements");
    } catch (e) {
      console.error(e);
      flash(e?.message || "Failed to publish announcement");
    } finally {
      setAnnLoading(false);
    }
  }

  async function onSendComms() {
    const msg = commsMessage.trim();
    if (!msg) return flash("Please write a message.");

    if (targetMode === "individual" && !targetLearnerId) {
      return flash("Select a learner to message.");
    }

    try {
      setCommsSending(true);

      if (commsTab !== "notifications") {
        flash("SMS/Email sending UI is ready — connect your backend comms endpoint to enable it.");
        return;
      }

      if (targetMode === "church") {
        flash("Church-wide broadcast is UI-ready — add a backend endpoint to send to the whole church scope.");
        return;
      }

      await onRemind(targetLearnerId, msg);

      setCommsSubject("");
      setCommsMessage("");
    } finally {
      setCommsSending(false);
    }
  }

  /* ---------------------------------- layout --------------------------------- */
  const Sidebar = (
    <aside className="h-full flex flex-col">
      <div className="px-5 py-5 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-sm">
            <User className="text-white" size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-extrabold text-slate-900 truncate">Instructor</div>
            <div className="text-xs text-slate-600 truncate">Church scope</div>
          </div>
        </div>
      </div>

      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {SECTIONS.map(({ id, label, icon: Icon }) => {
          const isActive = active === id;
          return (
            <button
              key={id}
              onClick={() => {
                setActive(id);
                setSidebarOpen(false);
              }}
              className={[
                "w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left transition",
                isActive
                  ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow"
                  : "hover:bg-orange-50 text-slate-700",
              ].join(" ")}
            >
              <span className="flex items-center gap-3">
                <span
                  className={[
                    "h-9 w-9 rounded-xl flex items-center justify-center",
                    isActive ? "bg-white/15" : "bg-white border border-slate-200",
                  ].join(" ")}
                >
                  <Icon size={18} className={isActive ? "text-white" : "text-orange-600"} />
                </span>
                <span className="font-semibold">{label}</span>
              </span>
              <ChevronRight size={16} className={isActive ? "text-white/90" : "text-slate-400"} />
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-200 bg-white">
        <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50 p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-9 w-9 rounded-xl bg-white flex items-center justify-center border border-orange-100">
              <Building2 size={18} className="text-orange-600" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-900 truncate">{churchInfo.churchName}</div>
              <div className="text-xs text-slate-600">
                {churchInfo.districtName ? `${churchInfo.districtName} • ` : ""}
                {churchInfo.conferenceName || "VOP-LMS"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
      {/* Mobile overlay + drawer */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="min-h-screen grid lg:grid-cols-[320px_1fr]">
        {/* Sidebar (desktop) */}
        <div className="hidden lg:block sticky top-0 h-screen border-r border-slate-200 bg-white/70 backdrop-blur">
          {Sidebar}
        </div>

        {/* Sidebar (mobile drawer) */}
        <div
          className={[
            "lg:hidden fixed z-50 inset-y-0 left-0 w-[85vw] max-w-sm bg-white shadow-2xl border-r border-slate-200 transform transition-transform duration-200",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
          ].join(" ")}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white/80 backdrop-blur">
            <div className="font-extrabold text-slate-900">Instructor</div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="h-10 w-10 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
            >
              <X size={18} className="text-slate-700" />
            </button>
          </div>
          {Sidebar}
        </div>

        {/* Main */}
        <div className="min-w-0">
          {/* Topbar */}
          <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
            <div className="px-4 md:px-8 py-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden h-11 w-11 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
                  aria-label="Open menu"
                >
                  <Menu size={18} className="text-slate-700" />
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-lg md:text-xl font-extrabold text-slate-900">
                      Instructor Dashboard
                    </h1>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700">
                      Church scope
                    </span>
                    {loadingLearners ? (
                      <span className="text-xs text-slate-500">Loading…</span>
                    ) : (
                      <span className="text-xs text-slate-500">{learners.length} learners</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600">{churchInfo.churchName}</div>
                </div>

                <div className="hidden md:flex items-center gap-2">
                  <button
                    onClick={loadLearners}
                    className="h-11 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-2 font-semibold text-slate-700"
                  >
                    <RefreshCw size={18} />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Global Search */}
              <div className="mt-4">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-3 py-2 shadow-sm">
                  <Search size={18} className="text-slate-500" />
                  <input
                    value={globalQ}
                    onChange={(e) => setGlobalQ(e.target.value)}
                    placeholder="Search learners, email, phone, IDs..."
                    className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                  />
                  {!!globalQ && (
                    <button
                      onClick={() => setGlobalQ("")}
                      className="h-8 w-8 rounded-xl hover:bg-slate-50 flex items-center justify-center"
                      aria-label="Clear search"
                    >
                      <X size={16} className="text-slate-500" />
                    </button>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>Tip: Search works across the whole dashboard — learners, church fields, and IDs.</span>
                  <div className="md:hidden">
                    <button
                      onClick={loadLearners}
                      className="inline-flex items-center gap-1 text-orange-700 font-semibold"
                    >
                      <RefreshCw size={14} /> Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Toast / Error */}
              <div className="mt-3 space-y-2">
                {!!toast && (
                  <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-xl text-sm">
                    <CheckCircle2 size={16} />
                    {toast}
                  </div>
                )}
                {!!err && (
                  <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-xl text-sm">
                    <AlertCircle size={16} />
                    {err}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="px-4 md:px-8 py-6 md:py-8 space-y-6">
            {active === "overview" && (
              <>
                {/* KPI cards */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Kpi icon={Users} label="Total Learners" value={stats.total} tone="orange" />
                  <Kpi icon={TrendingUp} label="Active" value={stats.activeCount} tone="amber" />
                  <Kpi icon={Clock} label="Dormant" value={stats.dormant} tone="slate" />
                  <Kpi icon={Award} label="Graduated" value={stats.completed} tone="emerald" />
                </section>

                {/* Church & Insights */}
                <section className="grid lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200 shadow-sm p-5 md:p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-bold text-slate-900">Church Learning Health</div>
                        <div className="text-xs text-slate-500">Snapshot based on learner activity & progress</div>
                      </div>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700">
                        Avg progress: {stats.avgProgress}%
                      </span>
                    </div>

                    <div className="mt-5">
                      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500"
                          style={{ width: `${stats.avgProgress}%` }}
                        />
                      </div>

                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <MiniStat label="Last active" value={stats.lastActive} />
                        <MiniStat label="Active learners" value={`${stats.activeCount}`} />
                        <MiniStat label="Dormant learners" value={`${stats.dormant}`} />
                        <MiniStat label="Graduates" value={`${stats.completed}`} />
                      </div>

                      <div className="mt-5 p-4 rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50">
                        <div className="text-sm font-bold text-slate-900">Quick action</div>
                        <div className="text-xs text-slate-600 mt-1">
                          Open learners and message someone who is dormant to re-engage.
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            onClick={() => setActive("learners")}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow-sm hover:from-orange-600 hover:to-amber-600 transition"
                          >
                            View learners
                          </button>
                          <button
                            onClick={() => setActive("comms")}
                            className="px-4 py-2 rounded-xl bg-white border border-slate-200 font-semibold text-slate-700 hover:bg-slate-50 transition"
                          >
                            Send message
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 md:p-6">
                    <div className="flex items-center gap-2">
                      <Building2 size={18} className="text-orange-600" />
                      <div className="text-sm font-extrabold text-slate-900">Scope info</div>
                    </div>
                    <div className="mt-4 space-y-3 text-sm">
                      <InfoRow label="Church" value={churchInfo.churchName} />
                      <InfoRow label="District" value={churchInfo.districtName || "—"} />
                      <InfoRow label="Conference" value={churchInfo.conferenceName || "—"} />
                      <InfoRow label="Access" value="Learners within your church only" />
                    </div>

                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-bold text-slate-700">Note</div>
                      <div className="text-xs text-slate-600 mt-1">
                        SMS/Email sending UI is ready below. Connect your comms backend endpoints to enable sending.
                      </div>
                    </div>
                  </div>
                </section>

                {/* Course analytics preview */}
                <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 md:p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold text-slate-900">Course enrollment</div>
                      <div className="text-xs text-slate-500">Based on available learner/course fields (best-effort)</div>
                    </div>
                    <button
                      onClick={() => setActive("courses")}
                      className="text-sm font-semibold text-orange-700 hover:text-orange-800"
                    >
                      View details
                    </button>
                  </div>

                  {courseBuckets.length === 0 ? (
                    <div className="mt-4 text-sm text-slate-600">
                      No per-course data found in learner rows (that’s okay). When your API returns per-course
                      enrollment/progress, this section will automatically populate.
                    </div>
                  ) : (
                    <div className="mt-4 grid md:grid-cols-3 gap-3">
                      {courseBuckets.slice(0, 6).map((b) => (
                        <div key={b.slug} className="rounded-2xl border border-slate-200 p-4 hover:bg-slate-50 transition">
                          <div className="font-bold text-slate-900 capitalize">{b.title}</div>
                          <div className="mt-2 text-sm text-slate-600">
                            Enrolled: <span className="font-semibold text-slate-900">{b.enrolled}</span>
                          </div>
                          <div className="text-sm text-slate-600">
                            Active: <span className="font-semibold text-slate-900">{b.active}</span>
                          </div>
                          <div className="text-sm text-slate-600">
                            Graduated: <span className="font-semibold text-slate-900">{b.graduated}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}

            {active === "learners" && (
              <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-lg font-extrabold text-slate-900">Learners</div>
                    <div className="text-sm text-slate-600">Expand a learner to see more details and take action.</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700"
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="dormant">Dormant</option>
                      <option value="graduated">Graduated</option>
                    </select>

                    <button
                      onClick={loadLearners}
                      className="px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 inline-flex items-center gap-2"
                    >
                      <RefreshCw size={16} />
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="mt-5 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr>
                        <th className="p-3 text-left font-bold">Learner</th>
                        <th className="p-3 text-left font-bold">Email</th>
                        <th className="p-3 text-center font-bold">Progress</th>
                        <th className="p-3 text-center font-bold">Last active</th>
                        <th className="p-3 text-right font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingLearners ? (
                        <tr>
                          <td colSpan={5} className="p-6 text-center text-slate-600">
                            Loading learners…
                          </td>
                        </tr>
                      ) : filteredLearners.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-10 text-center text-slate-600">
                            No learners match your filters/search.
                          </td>
                        </tr>
                      ) : (
                        filteredLearners.map((l, i) => {
                          const id = safeStr(l?.id || l?.user_id || l?.clerk_id || "");
                          const rowKey = keyOf(l, i);
                          const isGrad = !!l.graduated;
                          const prog = pct(l.progress);
                          const isDormant = !isGrad && prog === 0;
                          const isOpen = expanded === rowKey;

                          return (
                            <Fragment key={rowKey}>
                              <tr className="border-b border-slate-100 hover:bg-slate-50/60 transition">
                                <td className="p-3">
                                  <div className="flex items-center gap-3">
                                    <button
                                      onClick={() => setExpanded(isOpen ? null : rowKey)}
                                      className="h-9 w-9 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
                                      aria-label="Expand learner"
                                    >
                                      {isOpen ? (
                                        <ChevronDown size={16} className="text-slate-700" />
                                      ) : (
                                        <ChevronRight size={16} className="text-slate-700" />
                                      )}
                                    </button>
                                    <div className="min-w-0">
                                      <div className="font-bold text-slate-900 truncate">{nameOf(l)}</div>
                                      <div className="text-xs text-slate-500 truncate">
                                        {isGrad ? "Graduated" : isDormant ? "Dormant" : "Active"}
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                <td className="p-3 text-slate-700">{emailOf(l)}</td>

                                <td className="p-3 text-center">
                                  <div className="inline-flex items-center gap-2">
                                    <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                                      <div
                                        className="h-full bg-gradient-to-r from-orange-500 to-amber-500"
                                        style={{ width: `${prog}%` }}
                                      />
                                    </div>
                                    <span className="font-semibold text-slate-700">{prog}%</span>
                                  </div>
                                </td>

                                <td className="p-3 text-center text-slate-600">
                                  {fmtDate(l.lastActive || l.last_active)}
                                </td>

                                <td className="p-3 text-right">
                                  <div className="inline-flex items-center gap-2 justify-end">
                                    <button
                                      onClick={() => {
                                        setActive("comms");
                                        setTargetMode("individual");
                                        setCommsTab("notifications");
                                        setTargetLearnerId(id);
                                        setCommsSubject("");
                                        setCommsMessage("");
                                      }}
                                      className="px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold inline-flex items-center gap-2"
                                    >
                                      <Send size={16} className="text-orange-600" />
                                      Message
                                    </button>

                                    {isDormant && id && (
                                      <button
                                        onClick={() => onRemind(id)}
                                        className="px-3 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold hover:from-orange-600 hover:to-amber-600 inline-flex items-center gap-2 shadow-sm"
                                      >
                                        <Bell size={16} />
                                        Remind
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>

                              {isOpen && (
                                <tr className="bg-slate-50/60 border-b border-slate-100">
                                  <td colSpan={5} className="p-4">
                                    <div className="grid md:grid-cols-3 gap-3">
                                      <DetailCard label="Learner ID" value={id || "—"} />
                                      <DetailCard label="Phone" value={l.phone || l.phone_number || "—"} />
                                      <DetailCard label="Joined" value={fmtDate(l.created_at)} />
                                    </div>

                                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
                                      <div className="text-sm font-bold text-slate-900">Notes</div>
                                      <div className="text-sm text-slate-600 mt-1">
                                        Expandable details are “best effort” based on fields returned by your API. If you
                                        want richer learner profiles (course-by-course progress, attempts, certificates),
                                        expose those fields from the instructor learners endpoint.
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* (rest of your sections unchanged) */}
            {/* KEEP your comms/announcements/reports/courses sections exactly as you had them */}
            {/* Footer (shared component) */}
            <div className="pt-4">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- small UI pieces ---------------------------- */
function Kpi({ icon: Icon, label, value, tone = "orange" }) {
  const toneMap = {
    orange: "from-orange-500 to-amber-500",
    amber: "from-amber-500 to-orange-500",
    emerald: "from-emerald-500 to-green-500",
    slate: "from-slate-500 to-slate-700",
  };
  const grad = toneMap[tone] || toneMap.orange;

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold text-slate-600">{label}</div>
          <div className="mt-1 text-3xl font-extrabold text-slate-900">{value}</div>
        </div>
        <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center shadow-sm`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="text-xs font-bold text-slate-600">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-xs font-bold text-slate-600">{label}</div>
      <div className="text-sm font-semibold text-slate-900 text-right">{value}</div>
    </div>
  );
}

function DetailCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-bold text-slate-600">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900 break-words">{value}</div>
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-2 rounded-2xl border text-sm font-semibold inline-flex items-center gap-2 transition",
        active
          ? "border-orange-300 bg-orange-50 text-orange-800"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
      ].join(" ")}
    >
      <Icon size={16} className={active ? "text-orange-700" : "text-slate-500"} />
      {label}
    </button>
  );
}

function ReportCard({ title, desc, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="rounded-3xl border border-slate-200 bg-white p-5 text-left hover:bg-slate-50 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-extrabold text-slate-900">{title}</div>
          <div className="text-sm text-slate-600 mt-1">{desc}</div>
        </div>
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-sm">
          <Icon size={18} className="text-white" />
        </div>
      </div>
    </button>
  );
}
